import {
  checkIsMastraErrorToolOutput,
  type MastraErrorToolOutput,
} from "@repo/app-types/agents/mastraErrorToolOutput";
import {
  checkIsNoResultToolOutput,
  type NoResultToolOutput,
} from "@repo/app-types/agents/noResultToolOutput";
import type { PlaybookProposalResult } from "@repo/app-types/entities/playbookConfigurationTools";
import { ChatToolBase, type ToolComponent } from "@repo/ui/chat/chatToolCall";

/** Append the proposed node's name to the transcript line when it has one. */
export function buildProposalTitle(
  base: string,
  nodeName: string | undefined,
): string {
  return nodeName ? `${base}: "${nodeName}"` : base;
}

/**
 * What the suggestion was measured against. A sample size of zero means the
 * playbook has no graded call history, which the line says plainly rather than
 * implying it was checked at scale.
 */
export function buildProposalMeasurementDetail(
  sampleSize: number | undefined,
): string {
  if (typeof sampleSize !== "number" || sampleSize === 0) {
    return "created without measurement — no graded call history";
  }
  return `measured on ${sampleSize.toLocaleString()} sampled interactions`;
}

/**
 * How one proposal tool describes itself in the transcript. Everything else a
 * proposal card shows comes from the shared proposal result, so a new tool
 * supplies only these strings.
 */
export interface PlaybookProposalToolCopy<
  Output extends PlaybookProposalResult,
> {
  /** Shown while the call is in flight, e.g. "Analyzing a criterion". */
  running: string;
  /** Completes "An error occurred while …", e.g. "analyzing the criterion". */
  failed: string;
  /** What the turn proposed, e.g. `Proposed new criteria: "…"`. */
  proposalTitle: (output: Output) => string;
  /**
   * What the proposal was measured against, appended after a separator where
   * there is one. A delete runs no sample, so it has nothing to report.
   */
  detail?: (output: Output) => string | undefined;
}

/**
 * The one renderer for every playbook proposal tool: a status line throughout.
 * The transcript's job here is provenance — what the agent did and what it
 * measured — while acting on a proposal belongs to the chat's proposal list,
 * which tracks live state. A card here would duplicate the list's row and
 * carry a Review button frozen at turn time, which goes dead the moment the
 * proposal is applied or goes stale.
 */
export function buildPlaybookProposalTool<
  Output extends PlaybookProposalResult,
>(
  copy: PlaybookProposalToolCopy<Output>,
): ToolComponent<unknown, Output | MastraErrorToolOutput | NoResultToolOutput> {
  return function PlaybookProposalTool(props) {
    if (props.status.type === "running") {
      return <ChatToolBase status={props.status}>{copy.running}</ChatToolBase>;
    }

    // An expected condition (node not found, no sampleable calls, ...) — the
    // agent's own message explains it, so surface it as a completed call.
    if (checkIsNoResultToolOutput(props.output)) {
      return (
        <ChatToolBase status={props.status}>
          {props.output.message}
        </ChatToolBase>
      );
    }

    if (checkIsMastraErrorToolOutput(props.output)) {
      return (
        <ChatToolBase status={props.status}>
          An error occurred while {copy.failed}
        </ChatToolBase>
      );
    }

    const output = props.output;
    if (props.status.type !== "complete" || !output) {
      return <ChatToolBase status={props.status}>{copy.running}</ChatToolBase>;
    }

    const detail = copy.detail?.(output);
    return (
      <ChatToolBase status={props.status}>
        {copy.proposalTitle(output)}
        {detail ? ` · ${detail}` : ""}
      </ChatToolBase>
    );
  };
}
